// ---------------------------------------------------------------------------
// Merging multi-take recordings into one decodable audio file.
//
// Every press of Record/Resume creates a fresh MediaRecorder, so an answer
// recorded in several takes is several independent webm files. Concatenating
// those bytes is NOT a valid webm — decoders (Web Audio, Whisper, Azure
// Speech) read only the first take, which silently drops everything said
// after a resume. This module decodes each take with Web Audio and re-encodes
// the lot as a single mono 16 kHz WAV: valid everywhere the app transcribes
// (Whisper decodes it via decodeAudioData; Azure Speech takes WAV natively)
// and playable in <audio>.
// ---------------------------------------------------------------------------

/** Whisper's native rate; ample for speech playback and keeps WAVs small. */
const MERGE_SAMPLE_RATE = 16000;

/** Decode one recorded take to mono Float32 PCM at MERGE_SAMPLE_RATE. */
async function decodeToMonoPcm(blob: Blob): Promise<Float32Array> {
  const probe = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await probe.decodeAudioData(await blob.arrayBuffer());
  } finally {
    void probe.close();
  }
  const frames = Math.max(1, Math.ceil(decoded.duration * MERGE_SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, frames, MERGE_SAMPLE_RATE);
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0);
}

export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  let off = 44;
  for (let i = 0; i < samples.length; i++, off += 2) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(off, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Merge recorded takes into one WAV blob. A single take is returned as-is
 * (no re-encode, no quality loss — the overwhelmingly common case).
 * Throws if a take can't be decoded; callers fall back to the raw bytes.
 */
export async function mergeRecordings(segments: Blob[]): Promise<Blob> {
  if (segments.length === 1) return segments[0];
  const parts: Float32Array[] = [];
  for (const segment of segments) parts.push(await decodeToMonoPcm(segment));
  const total = parts.reduce((n, p) => n + p.length, 0);
  const all = new Float32Array(total);
  let offset = 0;
  for (const part of parts) {
    all.set(part, offset);
    offset += part.length;
  }
  return encodeWav(all, MERGE_SAMPLE_RATE);
}
